import { useState, useEffect } from "react";
import { client } from "@service/sanity";
import { hasBackPocketPrescription, getBackPocketPrescriptionIds } from "@constants/antibiotics";
import recommendationQuery from "@groq/queries/recommendation";

/**
 * Custom hook to fetch back-pocket prescription recommendations
 * Reuses the exact recommendationQuery for consistency
 */
const useBackPocketRecommendations = (recommendationId: string | null) => {
  const [backPocketRecommendations, setBackPocketRecommendations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!recommendationId) {
      return;
    }

    if (!hasBackPocketPrescription(recommendationId)) {
      return;
    }

    const backPocketIds = getBackPocketPrescriptionIds(recommendationId);
    
    if (!backPocketIds || backPocketIds.length === 0) {
      return;
    }

    const fetchBackPocketRecommendations = async () => {
      setIsLoading(true);
      
      try {
        const promises = backPocketIds.map(id => {
          const { query, params } = recommendationQuery(id);
          return client.fetch(query, params);
        });
        
        const results = await Promise.all(promises);
        const recommendations = results.map(result => result[0]).filter(Boolean);
        
        setBackPocketRecommendations(recommendations);
        
      } catch (error) {
        console.error('Failed to fetch back-pocket recommendations:', error);
        setBackPocketRecommendations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBackPocketRecommendations();
  }, [recommendationId]);

  return {
    backPocketRecommendations,
    isLoading
  };
};

export default useBackPocketRecommendations;